import type { FileSystemTree, WebContainerProcess } from "@webcontainer/api";
import type {
  CodeExecutionHookParams,
  CodeExecutionHookReturn,
} from "../useProvider";

import { useCallback, useEffect, useRef, useState } from "react";

import { isFeatureEnabled } from "@editor/infra/featureFlags";
import { webContainerRouter } from "@editor/trpc/webContainer";

import { skipToken } from "@tanstack/react-query";
import { WebContainer } from "@webcontainer/api";

/**
 * Message we show when mounting additional files for incremental updates, e.g.
 * modifiers. This is a constant because we check for it in logs to implement loading
 * states in some cases
 */
export const MOUNTING_ADDITONAL_FILES_MESSAGE = "Mounting additional files...";

const EXPECTED_PORT = 3000;
// NOTE (Gabe 2025-07-16): This is supposed to prevent interactive terminal
// control characters from being spit out of the node processes we run. It
// doesn't seem to work for next dev, so we also stripAnsiCodes using the
// function below.
const DUMB_TERMINAL_ENVS = {
  NO_COLOR: "1", // Standard opt-out for colored output
  FORCE_COLOR: "0", // Ensures tools like chalk suppress color
  TERM: "dumb", // Prevents terminal formatting
} as const;

export function useWebContainerHook({
  projectId,
  processLogMessage,
  forceSkip,
}: CodeExecutionHookParams): CodeExecutionHookReturn & {
  mountAdditionalFiles: (fileTree: FileSystemTree) => Promise<void>;
  exportFileTree: (exportDir: "full" | "workdir") => Promise<FileSystemTree>;
} {
  const isServerSandboxEnabled = isFeatureEnabled("use-server-sandbox");
  const { data: fileTree } = webContainerRouter.getFileTree.useQuery(
    projectId && !forceSkip ? { projectId } : skipToken,
  );

  const [webContainer, setWebContainer] = useState<WebContainer | undefined>(
    undefined,
  );

  const [error, setError] = useState<unknown | undefined>(undefined);
  const [webContainerUrl, setWebContainerUrl] = useState<string | undefined>(
    undefined,
  );

  const devProcessRef = useRef<WebContainerProcess | null>(null);

  // Effect 1: Boot WebContainer and set up listeners ONCE, and clean up on
  // unmount
  useEffect(() => {
    let webContainerInstance: WebContainer | undefined;
    async function bootWebContainer() {
      if (!projectId) {
        return;
      }
      try {
        processLogMessage(`Booting WebContainer for project ${projectId}...`);
        webContainerInstance = await WebContainer.boot();

        webContainerInstance.on("error", (error) => {
          processLogMessage(`WebContainer error: ${error.message}`);
        });

        webContainerInstance.on("server-ready", (port, url) => {
          processLogMessage(`Server ready at ${url}`);

          if (port !== EXPECTED_PORT) {
            return;
          }

          setWebContainerUrl(url);
        });

        setWebContainer(webContainerInstance);

        processLogMessage("WebContainer booted successfully.");
      } catch (error) {
        processLogMessage(
          `Error booting WebContainer: ${error instanceof Error ? error.message : String(error)}`,
        );
        setError(error);
        setWebContainer(undefined);
      }
    }

    void bootWebContainer();

    return () => {
      if (webContainerInstance) {
        setWebContainerUrl(undefined);
        setError(null);
        if (devProcessRef.current) {
          devProcessRef.current.kill();
          devProcessRef.current = null;
        }
        try {
          webContainerInstance.teardown();
        } catch (error) {
          processLogMessage(
            `Error during WebContainer teardown: ${error instanceof Error ? error.message : String(error)}`,
          );
          setError(error);
        }
        setWebContainer(undefined);
      }
    };
  }, [processLogMessage, projectId]);

  const mountFiles = useCallback(
    async (fileTree: FileSystemTree) => {
      if (!fileTree || !webContainer) {
        return;
      }

      try {
        processLogMessage("Mounting files...");
        await webContainer.mount(fileTree);
        processLogMessage("Files mounted successfully.");
      } catch (error) {
        console.error("Failed to mount files:", error);
        processLogMessage(
          `Error mounting files: ${error instanceof Error ? error.message : String(error)}`,
        );
      }
    },
    [processLogMessage, webContainer],
  );

  const runNpmInstall = useCallback(async () => {
    if (!webContainer) {
      return;
    }

    try {
      processLogMessage("Running npm install...");
      const installProcess = await webContainer.spawn("npm", ["install"], {
        env: DUMB_TERMINAL_ENVS,
      });
      void installProcess.output.pipeTo(
        new WritableStream({
          write(data) {
            processLogMessage(data);
          },
        }),
      );
      const installExitCode = await installProcess.exit;
      if (installExitCode !== 0) {
        processLogMessage(
          `npm install failed with exit code: ${installExitCode}`,
        );
        return false;
      }
      processLogMessage("npm install completed.");
      return true;
    } catch (error) {
      console.error("Failed to run npm install:", error);
      processLogMessage(
        `Error running npm install: ${error instanceof Error ? error.message : String(error)}`,
      );
      return false;
    }
  }, [processLogMessage, webContainer]);

  const startDevServer = useCallback(async () => {
    if (!webContainer) {
      return;
    }

    if (devProcessRef.current) {
      processLogMessage("Killing existing dev process...");
      setWebContainerUrl(undefined);
      devProcessRef.current.kill();
      devProcessRef.current = null;
    }

    try {
      processLogMessage("Starting development server...");
      devProcessRef.current = await webContainer.spawn(
        "npm",
        ["run", "dev:webpack"],
        {
          env: DUMB_TERMINAL_ENVS,
        },
      );
      void devProcessRef.current.output.pipeTo(
        new WritableStream({
          write(data) {
            processLogMessage(data);
          },
        }),
      );
    } catch (error) {
      console.error("Failed to start dev server:", error);
      processLogMessage(
        `Error starting dev server: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
  }, [processLogMessage, webContainer]);

  const setupProject = useCallback(async () => {
    if (!fileTree || !webContainer) {
      return;
    }

    try {
      await mountFiles(fileTree);
      const installSuccess = await runNpmInstall();
      if (installSuccess) {
        await startDevServer();
      }
    } catch (error) {
      console.error("Failed to setup project:", error);
      processLogMessage(
        `Error setting up project: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
  }, [
    fileTree,
    webContainer,
    processLogMessage,
    mountFiles,
    runNpmInstall,
    startDevServer,
  ]);

  // Effect 2: Handle initial setup (npm install + dev server) and file mounting
  useEffect(() => {
    if (!fileTree) {
      return;
    }

    if (!devProcessRef.current) {
      // Only run npm install and start dev server if this is the initial setup
      void setupProject();
    } else {
      processLogMessage(
        "Files updated - mounting without restarting dev server",
      );
      void mountFiles(fileTree);
    }
  }, [setupProject, fileTree, mountFiles, processLogMessage]);

  const mountAdditionalFiles = useCallback(
    async (fileTree: FileSystemTree) => {
      if (!webContainer) {
        processLogMessage(
          "Cannot mount additional files - WebContainer not booted",
        );
        return;
      }

      if (!isServerSandboxEnabled) {
        processLogMessage(MOUNTING_ADDITONAL_FILES_MESSAGE);
      }

      await webContainer.mount(fileTree);
      // HACK (Gabe 2025-08-11): For some reason tailwind doesn't always pick up
      // on classNames changes in the updated files. This results in the colors
      // or other utility classes being missing from the generated css file.
      // Reading and writing the globals.css file is a workaround to ensure that
      // tailwind watcher picks up the changes.
      const globalsCss = await webContainer.fs.readFile(
        "/app/globals.css",
        "utf-8",
      );
      await webContainer.fs.writeFile("/app/globals.css", globalsCss);
    },
    [processLogMessage, webContainer, isServerSandboxEnabled],
  );

  const exportFileTree = useCallback(
    async (exportDir: "full" | "workdir") => {
      if (!webContainer) {
        throw new Error("WebContainer not booted");
      }
      return webContainer.export(
        exportDir === "full" ? "/" : webContainer.workdir,
        {
          format: "json",
          excludes: ["**/node_modules/**/*", ".next/**/*"],
        },
      );
    },
    [webContainer],
  );

  return {
    url: webContainerUrl,
    error,
    mountAdditionalFiles,
    exportFileTree,
    restartDevServer: startDevServer,
    isLoading: !webContainerUrl,
  };
}
